import simplify from "@turf/simplify";

/**
 * Simplifies GeoJSON features based on the current zoom level
 *
 * @param geoJsonData The GeoJSON data to simplify
 * @param zoomLevel Current map zoom level
 * @returns Simplified GeoJSON data
 */
export function simplifyGeoJson(geoJsonData: any, zoomLevel: number): any {
  if (
    !geoJsonData ||
    !geoJsonData.features ||
    !Array.isArray(geoJsonData.features)
  ) {
    return geoJsonData;
  }

  // Determine tolerance based on zoom level
  // Lower zoom = more simplification
  let tolerance = 0.001; // Default

  if (zoomLevel < 6) {
    tolerance = 0.01; // High simplification for zoomed out views
  } else if (zoomLevel < 10) {
    tolerance = 0.005; // Medium simplification
  } else if (zoomLevel < 14) {
    tolerance = 0.002; // Lower simplification
  } else {
    tolerance = 0.001; // Minimal simplification when zoomed in
  }

  // For MultiPolygons with lots of vertices, use stronger simplification
  const complexFeatureCount = geoJsonData.features.filter((f: any) => {
    // Check if it's a complex feature (polygon with many coordinates)
    if (
      f.geometry &&
      (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon")
    ) {
      // Count vertices for MultiPolygon
      if (f.geometry.type === "MultiPolygon") {
        let vertexCount = 0;
        f.geometry.coordinates.forEach((poly: any) => {
          poly.forEach((ring: any) => {
            vertexCount += ring.length;
          });
        });
        return vertexCount > 1000; // Consider a feature complex if it has more than 1000 vertices
      }
      // Count vertices for regular Polygon
      return f.geometry.coordinates[0].length > 1000;
    }
    return false;
  }).length;

  // If we have complex features, increase simplification
  if (complexFeatureCount > 0) {
    tolerance *= 2;
  }


  // Clone the geoJsonData to avoid modifying the original
  const result = {
    ...geoJsonData,
    features: [],
  };

  // Process each feature
  result.features = geoJsonData.features.map((feature: any) => {
    // Skip simplification for points or small features
    if (
      !feature.geometry ||
      feature.geometry.type === "Point" ||
      feature.geometry.type === "MultiPoint"
    ) {
      return feature;
    }

    try {
      // For MultiPolygons, we need to be careful with simplification
      if (feature.geometry.type === "MultiPolygon") {
        // Apply stronger simplification for very complex MultiPolygons
        let vertexCount = 0;
        feature.geometry.coordinates.forEach((poly: any) => {
          poly.forEach((ring: any) => {
            vertexCount += ring.length;
          });
        });

        // Adjust tolerance based on complexity
        const adaptiveTolerance =
          vertexCount > 5000
            ? tolerance * 3
            : vertexCount > 2000
              ? tolerance * 2
              : tolerance;

        return simplify(feature, {
          tolerance: adaptiveTolerance,
          highQuality: true,
          mutate: false,
        });
      }

      // For other geometry types
      return simplify(feature, {
        tolerance: tolerance,
        highQuality: true,
        mutate: false,
      });
    } catch (error) {
      console.warn(`Error simplifying feature:`, error);
      return feature; // Return original if simplification fails
    }
  });

  return result;
}
